import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Check, Home } from 'lucide-react';
import { Button } from '../ui/button';
import { courseModules } from '../../data/courseData';
import { useProgress } from '../../context/ProgressContext';
import { CompletionScreen } from './CompletionScreen';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export function ModuleNavigation({ currentModule }) {
  const navigate = useNavigate();
  const { markModuleComplete, isModuleComplete, isQuizPassed, getOverallProgress } = useProgress();
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  
  const currentIndex = courseModules.findIndex(m => m.id === currentModule.id);
  const prevModule = currentIndex > 0 ? courseModules[currentIndex - 1] : null;
  const nextModule = currentIndex < courseModules.length - 1 ? courseModules[currentIndex + 1] : null;
  const isComplete = isModuleComplete(currentModule.id);
  const isLastModule = currentIndex === courseModules.length - 1;
  
  // Check if quiz is passed
  const quizPassed = isQuizPassed(currentModule.id);

  const handleComplete = () => {
    markModuleComplete(currentModule.id);
    
    // Check if this completes the entire course
    const newProgress = getOverallProgress();
    const willBeComplete = newProgress === 100 || 
      (courseModules.length - 1 === courseModules.filter((m, i) => 
        i === currentIndex || isModuleComplete(m.id)
      ).length);
    
    if (isLastModule || willBeComplete) {
      // Final module - show completion screen instead of auto-redirect
      toast.success('Course completed!', {
        description: 'Congratulations on finishing all modules!',
        duration: 4000,
      });
      
      // Delay showing completion screen slightly for toast to appear
      setTimeout(() => {
        setShowCompletionScreen(true);
      }, 500);
    } else {
      // Not the last module - proceed normally
      toast.success('Module completed!', {
        description: `Great job! Ready for "${nextModule?.shortTitle}"?`,
        duration: 4000,
      });

      setTimeout(() => navigate(`/module/${nextModule.slug}`), 500);
    }
  };

  return (
    <>
      <div className="bg-white rounded-2xl border-2 border-stone-200 p-6 md:p-8 mt-8">
        {/* Module progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {courseModules.map((m, index) => (
            <div
              key={m.id}
              className={cn(
                "w-3 h-3 rounded-full transition-all",
                index === currentIndex && "w-8 bg-primary",
                index !== currentIndex && isModuleComplete(m.id) && "bg-success",
                index !== currentIndex && !isModuleComplete(m.id) && "bg-stone-200"
              )}
            />
          ))}
        </div>

        {/* Navigation buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Previous button */}
          <div className="w-full sm:w-auto">
            {prevModule ? (
              <Button
                data-testid="nav-prev"
                variant="outline"
                onClick={() => navigate(`/module/${prevModule.slug}`)}
                className="w-full sm:w-auto rounded-full px-6 py-4 font-semibold nav-button"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                {prevModule.shortTitle}
              </Button>
            ) : (
              <Button
                data-testid="nav-home"
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full sm:w-auto rounded-full px-6 py-4 font-semibold nav-button"
              >
                <Home className="w-5 h-5 mr-2" />
                Home
              </Button>
            )}
          </div>

          {/* Complete / Next button */}
          <div className="w-full sm:w-auto">
            {!isComplete ? (
              <Button
                data-testid="complete-module"
                onClick={handleComplete}
                disabled={!quizPassed}
                className={cn(
                  "w-full sm:w-auto rounded-full px-8 py-6 text-lg font-bold nav-button transition-all",
                  quizPassed 
                    ? "bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md hover:shadow-lg" 
                    : "bg-stone-200 text-stone-500 cursor-not-allowed"
                )}
              >
                <Check className="w-5 h-5 mr-2" />
                {quizPassed ? 'Complete Module' : 'Pass Quiz First'}
              </Button>
            ) : nextModule ? (
              <Button
                data-testid="nav-next"
                onClick={() => navigate(`/module/${nextModule.slug}`)}
                className="w-full sm:w-auto bg-primary text-white hover:bg-primary-600 rounded-full px-8 py-6 text-lg font-bold nav-button"
              >
                {nextModule.shortTitle}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <Button
                data-testid="nav-certificate"
                onClick={() => navigate('/certificate')}
                className="w-full sm:w-auto bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-8 py-6 text-lg font-bold nav-button"
              >
                View Certificate
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>
        </div>

        {/* Help text */}
        {!isComplete && !quizPassed && (
          <p className="text-center text-text-muted text-sm mt-4">
            Complete the quiz above to unlock this module
          </p>
        )}
      </div>

      {/* Completion Screen Modal */}
      {showCompletionScreen && (
        <CompletionScreen onClose={() => setShowCompletionScreen(false)} />
      )}
    </>
  );
}
